// v8
// Path: frontend/src/components/SyncHub/SearchableSelect.jsx
// Inputs: options: Array<{ value, label, description, sublabel, isLive, isPrimaryKey, required, isCustom, isMapped, isPowered, type }>, value, onChange, placeholder, disabled, allowCustom, customPrompt, className, style
// Outputs: Rich searchable select dropdown component rendered via React Portal with guaranteed top-most stacking, flush unified panel design, instant auto-focus, keyboard navigation, and live metadata
// Summary: High-performance searchable dropdown featuring golden primary key styling, flush continuous panel design, memoized option filtering, persistent scroll preservation via useLayoutEffect, keyboard-isolated scrollIntoView, configurable custom option prompt, and event isolation preventing scroll resets.

import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';

// ── Memoized Dropdown Option Item ─────────────────────────────────────────────
const DropdownItem = React.memo(function DropdownItem({
    opt,
    idx,
    isSelected,
    isHighlighted,
    onSelect,
    onHighlight
}) {
    return (
        <div
            onClick={(e) => {
                e.stopPropagation();
                onSelect(opt.value);
            }}
            onMouseEnter={() => onHighlight(idx)}
            className={`px-3 py-2 cursor-pointer transition-colors flex items-start justify-between gap-2 text-xs select-none ${
                isSelected
                    ? 'bg-sky-950/60 text-sky-200'
                    : isHighlighted
                    ? 'bg-slate-800/80 text-slate-100'
                    : 'text-slate-300 hover:bg-slate-800/40'
            }`}
        >
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    {opt.isPowered && (
                        <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_6px_#34d399] shrink-0" />
                    )}
                    <span className={`font-mono font-bold ${
                        opt.isPrimaryKey
                            ? 'text-amber-300'
                            : opt.isPowered
                            ? 'text-emerald-300'
                            : opt.isCustom
                            ? 'text-purple-300'
                            : 'text-sky-400'
                    }`}>
                        {opt.label || opt.value}
                    </span>
                    {opt.isPowered && (
                        <span className="bg-emerald-500/20 text-emerald-300 text-[9px] px-1.5 py-0.2 rounded border border-emerald-500/40 shadow-[0_0_6px_rgba(16,185,129,0.3)] font-bold">
                            ACTIVE
                        </span>
                    )}
                    {opt.required && (
                        <span className="bg-rose-500/20 text-rose-400 text-[9px] px-1 py-0.2 rounded border border-rose-500/30 font-bold">
                            REQUIRED
                        </span>
                    )}
                    {opt.isCustom && (
                        <span className="bg-purple-500/20 text-purple-300 text-[9px] px-1 py-0.2 rounded border border-purple-500/30">
                            CUSTOM
                        </span>
                    )}
                    {opt.isMapped && (
                        <span className="bg-slate-700/60 text-slate-400 text-[9px] px-1 py-0.2 rounded">
                            MAPPED
                        </span>
                    )}
                    {opt.type && (
                        <span className="bg-slate-800 text-slate-400 text-[9px] px-1 py-0.2 rounded font-mono">
                            {opt.type}
                        </span>
                    )}
                    {opt.isPrimaryKey && (
                        <span className="inline-flex items-center gap-0.5 bg-amber-500/25 text-amber-300 text-[9px] px-1.5 py-0.2 rounded border border-amber-500/50 font-bold shadow-[0_0_6px_rgba(245,158,11,0.25)]">
                            <span className="material-symbols-outlined text-[10px] text-amber-400">vpn_key</span>
                            PK
                        </span>
                    )}
                    {opt.sqlType && (
                        <span className="bg-slate-800 text-slate-400 text-[9px] px-1 py-0.2 rounded">
                            {opt.sqlType}{opt.maxLength ? `(${opt.maxLength})` : ''}
                        </span>
                    )}
                    {opt.isLiveColumn && (
                        <span className="bg-emerald-500/20 text-emerald-400 text-[9px] px-1 py-0.2 rounded border border-emerald-500/30">
                            LIVE
                        </span>
                    )}
                </div>
                {opt.description && (
                    <p className="text-slate-400 text-[11px] truncate font-sans mt-0.5">
                        {opt.description}
                    </p>
                )}
                {opt.cobolName && opt.cobolName !== opt.value && (
                    <span className="text-slate-500 text-[10px] font-mono">
                        COBOL: {opt.cobolName}
                    </span>
                )}
            </div>
            {isSelected && (
                <span className="text-sky-400 text-xs font-bold shrink-0">✓</span>
            )}
        </div>
    );
});

// ── SearchableSelect Component ────────────────────────────────────────────────
export default function SearchableSelect({
    options = [],
    value = '',
    onChange,
    placeholder = 'Search or select...',
    disabled = false,
    allowCustom = false,
    customPrompt = 'Add / Use custom',
    className = '',
    style = {}
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightIndex, setHighlightIndex] = useState(0);
    const [dropdownPos, setDropdownPos] = useState(null);
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    // Scroll persistence ref to restore exact scroll offset across re-renders
    const listScrollTopRef = useRef(0);

    // Keyboard-only navigation flag to prevent mouseenter / scrolling from resetting scroll position
    const isKeyboardNavRef = useRef(false);

    // Find current selected item (loosely comparing numbers/strings so 1 === "1")
    const selectedOption = useMemo(() => {
        return options.find(o => String(o.value ?? '') === String(value ?? ''));
    }, [options, value]);

    // Memoize filtered options to prevent recalculation on unrelated render cycles
    const filteredOptions = useMemo(() => {
        if (!searchTerm.trim()) return options;
        const q = searchTerm.toLowerCase();
        return options.filter(opt => {
            return (
                (opt.label != null && String(opt.label).toLowerCase().includes(q)) ||
                (opt.value != null && String(opt.value).toLowerCase().includes(q)) ||
                (opt.description != null && String(opt.description).toLowerCase().includes(q)) ||
                (opt.sublabel != null && String(opt.sublabel).toLowerCase().includes(q))
            );
        });
    }, [options, searchTerm]);

    const isExactMatch = useMemo(() => {
        const trimmed = searchTerm.trim().toLowerCase();
        return options.some(o => 
            String(o.value ?? '').toLowerCase() === trimmed || 
            String(o.label ?? '').toLowerCase() === trimmed
        );
    }, [options, searchTerm]);

    const showCustomOption = allowCustom && searchTerm.trim().length > 0 && !isExactMatch;

    // Calculate fixed screen coordinates for top-most portal rendering (flush against trigger)
    const updatePosition = useCallback(() => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const showAbove = spaceBelow < 220 && spaceAbove > spaceBelow;

        setDropdownPos({
            top: showAbove ? undefined : rect.bottom,
            bottom: showAbove ? (window.innerHeight - rect.top) : undefined,
            left: rect.left,
            width: rect.width,
            showAbove
        });
    }, []);

    // Listen to window scroll & resize while ignoring scrolls originating within the dropdown portal itself
    useEffect(() => {
        if (isOpen) {
            updatePosition();
            const handleReposition = (e) => {
                // If scroll event is coming from inside the dropdown list, do not reposition or trigger state updates!
                if (e && dropdownRef.current && dropdownRef.current.contains(e.target)) {
                    return;
                }
                updatePosition();
            };
            window.addEventListener('scroll', handleReposition, true);
            window.addEventListener('resize', handleReposition);
            return () => {
                window.removeEventListener('scroll', handleReposition, true);
                window.removeEventListener('resize', handleReposition);
            };
        } else {
            setDropdownPos(null);
        }
    }, [isOpen, updatePosition]);

    // Handle clicks outside container or portal dropdown to close
    useEffect(() => {
        const handleClickOutside = (e) => {
            const inContainer = containerRef.current && containerRef.current.contains(e.target);
            const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
            if (!inContainer && !inDropdown) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Instantly focus search input when dropdown opens (strictly decoupled from dropdownPos updates)
    useEffect(() => {
        if (isOpen) {
            setHighlightIndex(0);
            listScrollTopRef.current = 0;
            const timer = setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                    inputRef.current.select?.();
                }
            }, 10);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    // Preserve and restore exact scroll position across re-renders
    useLayoutEffect(() => {
        if (listRef.current && listScrollTopRef.current > 0) {
            listRef.current.scrollTop = listScrollTopRef.current;
        }
    });

    // Only scroll highlighted element into view when explicitly navigating via KEYBOARD
    useEffect(() => {
        if (isKeyboardNavRef.current && listRef.current && highlightIndex >= 0 && listRef.current.children[highlightIndex]) {
            isKeyboardNavRef.current = false;
            listRef.current.children[highlightIndex].scrollIntoView({ block: 'nearest' });
        }
    }, [highlightIndex]);

    // Track scroll position on internal options list and stop event bubbling
    const handleListScroll = useCallback((e) => {
        e.stopPropagation();
        listScrollTopRef.current = e.currentTarget.scrollTop;
    }, []);

    // Mouse highlight handler that explicitly disables keyboard scrollIntoView
    const handleHighlightFromMouse = useCallback((idx) => {
        isKeyboardNavRef.current = false;
        setHighlightIndex(idx);
    }, []);

    // Select handler
    const handleSelect = useCallback((val) => {
        if (onChange) onChange(val);
        setIsOpen(false);
        setSearchTerm('');
        listScrollTopRef.current = 0;
    }, [onChange]);

    // Keyboard navigation
    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            isKeyboardNavRef.current = true;
            setHighlightIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            isKeyboardNavRef.current = true;
            setHighlightIndex(prev => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filteredOptions[highlightIndex]) {
                handleSelect(filteredOptions[highlightIndex].value);
            } else if (showCustomOption) {
                handleSelect(searchTerm.trim());
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setIsOpen(false);
            setSearchTerm('');
            listScrollTopRef.current = 0;
        }
    };

    const isPoweredActive = !!selectedOption?.isPowered;

    return (
        <div ref={containerRef} className={`relative inline-block ${className}`} style={{ minWidth: 200, ...style }}>
            {/* Trigger Button - Ultra-condensed h-7 (visually merges with dropdown when open) */}
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`w-full h-7 flex items-center justify-between text-left px-2 py-0 text-xs font-mono bg-slate-900 border text-slate-200 focus:outline-none disabled:opacity-50 transition-all ${
                    isPoweredActive
                        ? 'border-emerald-500/50 shadow-[0_0_8px_rgba(16,185,129,0.2)] hover:border-emerald-400 focus:border-emerald-400'
                        : isOpen
                            ? 'border-indigo-500'
                            : 'border-slate-700 hover:border-sky-500 focus:border-sky-400'
                } ${
                    isOpen
                        ? (dropdownPos?.showAbove
                            ? 'rounded-t-none border-t-transparent'
                            : 'rounded-b-none border-b-transparent')
                        : 'rounded'
                }`}
            >
                <span className="truncate pr-2">
                    {selectedOption ? (
                        <span className="flex items-center gap-1.5 truncate">
                            {isPoweredActive && (
                                <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_6px_#34d399] shrink-0" />
                            )}
                            <span className={`font-semibold ${isPoweredActive ? 'text-emerald-300' : 'text-sky-400'}`}>
                                {selectedOption.label || selectedOption.value}
                            </span>
                            {selectedOption.description && (
                                <span className="text-slate-400 font-sans text-[11px] truncate">({selectedOption.description})</span>
                            )}
                        </span>
                    ) : value ? (
                        <span className="font-semibold text-amber-300 font-mono truncate">{value}</span>
                    ) : (
                        <span className="text-slate-500 font-sans">{placeholder}</span>
                    )}
                </span>
                <span className="text-slate-500 text-[10px] ml-1">▼</span>
            </button>

            {/* Dropdown Menu Portal (visually unified flush panel with trigger) */}
            {isOpen && dropdownPos && typeof document !== 'undefined' && createPortal(
                <div
                    ref={dropdownRef}
                    onScroll={(e) => e.stopPropagation()}
                    onWheel={(e) => e.stopPropagation()}
                    onMouseEnter={(e) => e.stopPropagation()}
                    onMouseMove={(e) => e.stopPropagation()}
                    className={`fixed bg-slate-900 border shadow-2xl overflow-hidden flex flex-col z-[99999] ${
                        isPoweredActive ? 'border-emerald-500/50' : 'border-indigo-500'
                    } ${
                        dropdownPos.showAbove
                            ? 'rounded-t-lg rounded-b-none border-b-0 mb-0'
                            : 'rounded-b-lg rounded-t-none border-t-0 mt-0'
                    }`}
                    style={{
                        top: dropdownPos.top,
                        bottom: dropdownPos.bottom,
                        left: dropdownPos.left,
                        width: dropdownPos.width,
                        maxHeight: 320,
                        zIndex: 99999
                    }}
                >
                    {/* Search Input */}
                    <div className="p-2 border-b border-slate-800 bg-slate-950/80 flex items-center gap-2 shrink-0">
                        <span className="text-slate-400 text-xs">🔍</span>
                        <input
                            ref={inputRef}
                            type="text"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setHighlightIndex(0);
                                listScrollTopRef.current = 0;
                                if (listRef.current) listRef.current.scrollTop = 0;
                            }}
                            onKeyDown={handleKeyDown}
                            placeholder={allowCustom ? "Search field or type custom..." : "Type to filter..."}
                            className="w-full bg-transparent border-none text-xs text-white placeholder-slate-500 focus:outline-none font-mono"
                        />
                        {searchTerm && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSearchTerm('');
                                    setHighlightIndex(0);
                                    listScrollTopRef.current = 0;
                                    if (listRef.current) listRef.current.scrollTop = 0;
                                }}
                                className="text-slate-400 hover:text-white text-xs px-1"
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    {/* Options List with Scroll Preservation */}
                    <div
                        ref={listRef}
                        onScroll={handleListScroll}
                        onWheel={(e) => e.stopPropagation()}
                        className="overflow-y-auto flex-1 divide-y divide-slate-800/40"
                    >
                        {filteredOptions.length === 0 && !showCustomOption ? (
                            <div className="px-3 py-3 text-center text-slate-500 text-xs font-sans">
                                No matching options
                            </div>
                        ) : (
                            filteredOptions.map((opt, idx) => (
                                <DropdownItem
                                    key={opt.value + '_' + idx}
                                    opt={opt}
                                    idx={idx}
                                    isSelected={opt.value === value}
                                    isHighlighted={idx === highlightIndex}
                                    onSelect={handleSelect}
                                    onHighlight={handleHighlightFromMouse}
                                />
                            ))
                        )}

                        {/* Custom Option Tile */}
                        {showCustomOption && (
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelect(searchTerm.trim());
                                }}
                                className="px-3 py-2 cursor-pointer bg-amber-950/40 hover:bg-amber-900/50 text-amber-300 text-xs border-t border-slate-700/80 flex items-center gap-2 font-mono transition-colors select-none"
                            >
                                <span className="material-symbols-outlined text-[16px] text-amber-400">add_circle</span>
                                <div className="flex-1 truncate">
                                    {customPrompt}: <strong className="text-amber-200 underline">"{searchTerm.trim()}"</strong>
                                </div>
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
